import { AddressSuggestion } from '@/features/pos/components/CustomerProfile/types'
import {
    CustomerLookupAddressOption,
    CustomerLookupAddressSection,
    CustomerLookupCustomer,
} from '@/features/pos/components/CustomerProfile/types'
import { formatCustomerAddress } from '@/features/pos/components/CustomerProfile/utils/transforms'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface UseCustomerLookupAddressOptionsArgs {
    addressSuggestions: AddressSuggestion[]
    currentAddress?: Pick<CustomerLookupAddressOption, 'address' | 'coordinates'>
    debouncedAddress: string
    hasAddressSearch: boolean
    selectedCustomer?: CustomerLookupCustomer
}

interface UseCustomerLookupAddressOptionsResult {
    addressOptions: CustomerLookupAddressOption[]
    addressSections: CustomerLookupAddressSection[]
}

export const useCustomerLookupAddressOptions = ({
    addressSuggestions,
    currentAddress,
    debouncedAddress,
    hasAddressSearch,
    selectedCustomer,
}: UseCustomerLookupAddressOptionsArgs): UseCustomerLookupAddressOptionsResult => {
    const { t } = useTranslation()

    const addressOptions = useMemo<CustomerLookupAddressOption[]>(() => {
        const currentAddressValue = currentAddress?.address
        const normalizedCurrentAddress = currentAddressValue?.trim().toLowerCase() ?? null

        const currentAddressOptions: CustomerLookupAddressOption[] = currentAddressValue
            ? [
                  {
                      address: currentAddressValue,
                      coordinates: currentAddress.coordinates,
                      id: `current-${currentAddressValue}`,
                      isSelected: true,
                      kind: 'suggestion' as const,
                  },
              ]
            : []

        const customerAddressOptions: CustomerLookupAddressOption[] =
            selectedCustomer?.addresses
                .flatMap((address) => {
                    const formattedAddress = formatCustomerAddress(address)

                    if (!formattedAddress) return []

                    return [
                        {
                            address: formattedAddress,
                            coordinates:
                                address.lat != null && address.lng != null
                                    ? { latitude: address.lat, longitude: address.lng }
                                    : null,
                            id: address.uuid,
                            isSelected: false,
                            kind: 'customer' as const,
                        },
                    ]
                })
                .filter(
                    (address) =>
                        (!normalizedCurrentAddress ||
                            address.address.trim().toLowerCase() !== normalizedCurrentAddress) &&
                        (hasAddressSearch
                            ? address.address
                                  .toLowerCase()
                                  .includes(debouncedAddress.trim().toLowerCase())
                            : true)
                ) ?? []

        const addressSuggestionOptions = addressSuggestions
            .filter(
                (addressSuggestion) =>
                    !normalizedCurrentAddress ||
                    addressSuggestion.address.trim().toLowerCase() !== normalizedCurrentAddress
            )
            .map((addressSuggestion, index) => ({
                address: addressSuggestion.address,
                id: `${addressSuggestion.address}-${index}`,
                isSelected: false,
                isWhole: addressSuggestion.isWhole,
                kind: 'suggestion' as const,
            }))

        return [...currentAddressOptions, ...customerAddressOptions, ...addressSuggestionOptions]
    }, [addressSuggestions, currentAddress, debouncedAddress, hasAddressSearch, selectedCustomer])

    const addressSections = useMemo<CustomerLookupAddressSection[]>(() => {
        const currentAddressSection = currentAddress
            ? [
                  {
                      id: 'current-address',
                      options: addressOptions.filter((option) => option.id.startsWith('current-')),
                      title: t('pos.customerLookupModal.results.currentAddress'),
                  },
              ]
            : []

        const customerAddressSection = selectedCustomer
            ? [
                  {
                      id: 'customer-addresses',
                      options: addressOptions.filter(
                          (option) =>
                              option.kind === 'customer' && !option.id.startsWith('current-')
                      ),
                      title: t('pos.customerLookupModal.results.addressesFromLastOrders'),
                  },
              ]
            : []

        const suggestionSection = hasAddressSearch
            ? [
                  {
                      id: 'suggested-addresses',
                      options: addressOptions.filter(
                          (option) =>
                              option.kind === 'suggestion' && !option.id.startsWith('current-')
                      ),
                  },
              ]
            : []

        return [...currentAddressSection, ...customerAddressSection, ...suggestionSection].filter(
            (section) => section.options.length > 0
        )
    }, [addressOptions, currentAddress, hasAddressSearch, selectedCustomer, t])

    return { addressOptions, addressSections }
}
